import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Loader2 } from 'lucide-react';
import { passwordViolations, type PasswordViolation } from '@nbh/shared';
import { useAuth } from '../../auth/AuthProvider';
import { ApiError, api } from '../../lib/api';
import { PasswordField } from '../ui/PasswordField';

/**
 * Changing your own password (decisions D3, D4, D5).
 *
 * One component for both places it appears — account settings, and the forced-change
 * screen somebody lands on after an admin hands them a temporary password. The rules are
 * identical in both, including the current-password field, so two forms would be two
 * places for those rules to drift.
 *
 * The inline feedback comes from the same validator the server enforces
 * (`packages/shared`), so a password this screen accepts is one the route accepts. That
 * is the only reason showing rules client-side is safe: it is a courtesy, and the server
 * refuses independently.
 */
export function PasswordChangeForm({ onChanged }: { onChanged?: () => void }) {
  const { t } = useTranslation();
  const { user, refresh } = useAuth();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [serverViolations, setServerViolations] = useState<PasswordViolation[]>([]);
  const [done, setDone] = useState<number | null>(null);

  // Only once they have typed something. Listing every unmet rule against an empty field
  // greets somebody with a wall of red before they have done anything wrong.
  const live = newPassword
    ? passwordViolations(newPassword, {
        email: user?.email,
        // `currentPassword` is compared as typed here; the server compares against the
        // stored hash, which is the authoritative check and works even when this field is
        // blank on a screen that did not ask for it.
        currentPassword,
      })
    : [];

  const shown = serverViolations.length > 0 ? serverViolations : live;
  const mismatch = confirmPassword.length > 0 && confirmPassword !== newPassword;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;

    setBusy(true);
    setError(null);
    setServerViolations([]);

    try {
      const result = await api.changePassword({ currentPassword, newPassword, confirmPassword });

      // Re-read the session so `mustChangePassword` clears and the router lets go of the
      // forced-change screen. Without this the flag on the client stays stale until the
      // next natural probe, and the user sits on a screen they have already finished with.
      await refresh();

      setDone(result.otherSessionsRevoked);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      onChanged?.();
    } catch (caught) {
      if (caught instanceof ApiError && caught.violations?.length) {
        // The server refused on the policy. Show exactly what it objected to rather than
        // a generic failure — these are the same codes the live check renders.
        setServerViolations(caught.violations as PasswordViolation[]);
      } else if (caught instanceof ApiError && caught.status === 401) {
        setError(t('security.wrongCurrent'));
      } else if (caught instanceof ApiError && caught.status === 429) {
        setError(
          t('security.throttled', { count: Math.ceil((caught.retryAfterSeconds ?? 60) / 60) }),
        );
      } else {
        setError(t('security.failed'));
      }
    } finally {
      setBusy(false);
    }
  }

  if (done !== null) {
    return (
      <p role="status" className="flex items-start gap-2 text-sm text-emerald-300">
        <Check className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        {/* Says what happened to the other devices, because signing them out is a
            consequence somebody should not have to discover later. */}
        {t('security.changed', { count: done })}
      </p>
    );
  }

  return (
    <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
      <PasswordField
        label={t('security.current')}
        value={currentPassword}
        onChange={setCurrentPassword}
        autoComplete="current-password"
        required
      />

      <PasswordField
        label={t('security.new')}
        value={newPassword}
        onChange={setNewPassword}
        autoComplete="new-password"
        required
      />

      <PasswordField
        label={t('security.confirm')}
        value={confirmPassword}
        onChange={setConfirmPassword}
        autoComplete="new-password"
        required
      />

      {mismatch && (
        <p role="alert" className="text-sm text-red-400">
          {t('security.mismatch')}
        </p>
      )}

      {shown.length > 0 && (
        <ul className="space-y-1 text-xs text-amber-200">
          {shown.map((violation) => (
            <li key={violation}>{t(`security.rule.${violation}`)}</li>
          ))}
        </ul>
      )}

      {error && (
        <p role="alert" className="text-sm text-red-400">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={busy || !currentPassword || !newPassword || mismatch || live.length > 0}
        className="mt-1 flex min-h-[56px] w-full items-center justify-center gap-2 rounded-xl bg-brookfields-green ring-1 ring-brookfields-gold/70 text-base font-semibold text-white disabled:bg-brookfields-green-dark disabled:text-slate-400 disabled:ring-brookfields-gold-dark"
      >
        {busy && <Loader2 className="size-5 animate-spin" aria-hidden="true" />}
        {busy ? t('security.saving') : t('security.save')}
      </button>
    </form>
  );
}
